<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div
      class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <div class="d-sm-flex align-items-center">
        <h5 class="mb-0 fw-bold">Demandes de commande</h5>
      </div>
      <div class="d-flex align-items-center gap-3">
        <!-- Filtre statut avec Multiselect -->
        <div style="min-width:180px">
          <Multiselect :options="['En attente', 'Validée', 'Rejetée']" :searchable="true" v-model="filtreStatut"
            placeholder="Tous les statuts" @select="charger(1)" @clear="() => { filtreStatut = ''; charger(1); }" />
        </div>
        <!-- Recherche -->
        <form class="search-bg svg-color pt-3" @submit.prevent="charger(1)">
          <input type="text" v-model="searchTerm" @keyup="charger(1)" class="form-control shadow-none text-black"
            placeholder="Référence, client, commercial..." />
          <button type="submit" class="bg-transparent text-primary transition p-0 border-0">
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </form>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25">
      <!-- Tableau -->
      <div class="table-responsive">
        <table class="table table-hover text-nowrap align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Référence</th>
              <th>Commercial</th>
              <th>Client</th>
              <th>Montant TTC</th>
              <th>Date</th>
              <th>Statut</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                <span class="ms-2 text-muted">Chargement...</span>
              </td>
            </tr>
            <tr v-else-if="demandes.length === 0">
              <td colspan="7" class="text-center py-4 text-muted">
                <i class="fa fa-inbox fa-2x d-block mb-2"></i>
                Aucune demande trouvée
              </td>
            </tr>
            <tr v-for="d in demandes" :key="d.id" v-else>
              <td>
                <span class="fw-semibold text-primary">{{ d.refCom || '#' + d.id }}</span>
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div class="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                    style="width:30px;height:30px;font-size:12px;font-weight:700;flex-shrink:0">
                    {{ initiales(d.user) }}
                  </div>
                  <span>{{ nomComplet(d.user) }}</span>
                </div>
              </td>
              <td>{{ nomComplet(d.client, true) }}</td>
              <td>
                <strong>{{ formatMontant(d.montTTC) }} FCFA</strong>
              </td>
              <td>{{ formatDate(d.createdAt) }}</td>
              <td>
                <span :class="['badge', badgeClass(d.statut)]">
                  {{ d.statut || 'En attente' }}
                </span>
              </td>
              <td>
                <div class="dropdown">
                  <button class="btn btn-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                    Actions
                  </button>
                  <ul class="dropdown-menu">
                    <li>
                      <router-link :to="{ name: 'ViewVente', params: { id: d.id } }"
                        class="dropdown-item d-flex align-items-center">
                        <i class="flaticon-eye me-2"></i>Détails
                      </router-link>
                    </li>
                    <li v-if="d.statut === 'En attente'">
                      <a class="dropdown-item d-flex align-items-center text-success" href="javascript:void(0);"
                        @click="valider(d)">
                        <i class="fa fa-check me-2"></i>Valider
                      </a>
                    </li>
                    <li v-if="d.statut === 'En attente'">
                      <a class="dropdown-item d-flex align-items-center text-danger" href="javascript:void(0);"
                        @click="ouvriRejeter(d)">
                        <i class="fa fa-times me-2"></i>Rejeter
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-area d-md-flex mt-15 justify-content-between align-items-center">
        <PaginationComponent :page="page" :totalPages="totalPages" :totalElements="totalElements" :limit="limit"
          @paginate="handlePaginate" />
      </div>
    </div> <!-- fin card-body -->
  </div> <!-- fin card -->

  <!-- Modal Rejeter -->
  <div class="modal fade" id="rejeterModal" tabindex="-1" ref="rejeterModalRef">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header border-0 pb-0">
          <h5 class="modal-title fw-bold">
            <i class="fa fa-times-circle text-danger me-2"></i>Rejeter la demande
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <p class="text-muted mb-3">
            Demande : <strong>{{ demandeSelectionnee?.refCom || '#' + demandeSelectionnee?.id }}</strong>
          </p>
          <label class="form-label fw-semibold">Motif du rejet <span class="text-danger">*</span></label>
          <textarea v-model="motifRejet" class="form-control" rows="4"
            placeholder="Expliquez pourquoi cette demande est rejetée..."></textarea>
        </div>
        <div class="modal-footer border-0 pt-0">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="fa fa-times me-1"></i> Annuler
          </button>
          <button type="button" class="btn btn-danger" @click="confirmerRejet" :disabled="!motifRejet.trim()">
            <i class="fa fa-check me-1"></i> Confirmer le rejet
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { Modal } from "bootstrap";
import ApiService from "@/services/ApiService";
import { error, success, monSwal } from "@/utils/utils";
import PaginationComponent from "@/components/Utilities/Pagination.vue";
import Multiselect from "@vueform/multiselect/src/Multiselect";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default defineComponent({
  name: "ListeDemandeCommande",
  components: { PaginationComponent, Multiselect },

  setup() {
    const demandes = ref<any[]>([]);
    const loading = ref(false);
    const page = ref(1);
    const totalPages = ref(0);
    const totalElements = ref(0);
    const limit = ref(10);
    const searchTerm = ref("");
    const filtreStatut = ref("");

    // Modal rejet
    const rejeterModalRef = ref<HTMLElement | null>(null);
    let rejeterModal: InstanceType<typeof Modal> | null = null;
    const demandeSelectionnee = ref<any>(null);
    const motifRejet = ref("");

    // ── Charger les demandes ───────────────────────────────────────────────
    const charger = async (p = page.value) => {
      loading.value = true;
      try {
        const params = new URLSearchParams({
          page: String(p),
          limit: String(limit.value),
        });
        if (searchTerm.value.trim()) params.append("motCle", searchTerm.value.trim());
        if (filtreStatut.value) params.append("statut", filtreStatut.value);

        const resp = await ApiService.get(`/demandes-commandes?${params.toString()}`);
        const data = resp.data.data;
        demandes.value = data.data ?? [];
        totalPages.value = data.totalPages ?? 0;
        totalElements.value = data.totalElements ?? 0;
        limit.value = data.limit ?? 10;
        page.value = p;
      } catch (err: any) {
        error(err.response?.data?.message || "Impossible de charger les demandes.");
      } finally {
        loading.value = false;
      }
    };

    const handlePaginate = ({ page_, limit_ }: { page_: number; limit_: number }) => {
      limit.value = limit_;
      charger(page_);
    };

    // ── Valider ────────────────────────────────────────────────────────────
    const valider = async (demande: any) => {
      const ref = demande.refCom || `#${demande.id}`;
      const ok = await monSwal(
        `Voulez-vous valider la demande ${ref} ?\n\nLe stock logique sera déduit automatiquement.`,
        "Oui, valider",
        "btn btn-success",
        "Annuler",
        "Validation annulée"
      );
      if (!ok) return;
      try {
        await ApiService.put(`/demandes-commandes/${demande.id}/valider`, {});
        success(`La demande ${ref} a été validée avec succès.`);
        charger();
      } catch (err: any) {
        error(err.response?.data?.message || "Impossible de valider la demande.");
      }
    };

    // ── Rejeter ────────────────────────────────────────────────────────────
    const ouvriRejeter = (demande: any) => {
      demandeSelectionnee.value = demande;
      motifRejet.value = "";
      if (!rejeterModal && rejeterModalRef.value) {
        rejeterModal = new Modal(rejeterModalRef.value);
      }
      rejeterModal?.show();
    };

    const confirmerRejet = async () => {
      if (!motifRejet.value.trim() || !demandeSelectionnee.value) return;
      try {
        await ApiService.put(`/demandes-commandes/${demandeSelectionnee.value.id}/rejeter`, {
          motifRejet: motifRejet.value.trim(),
        });
        success("La demande a été rejetée.");
        rejeterModal?.hide();
        charger();
      } catch (err: any) {
        error(err.response?.data?.message || "Impossible de rejeter la demande.");
      }
    };

    // ── Helpers ────────────────────────────────────────────────────────────
    const nomComplet = (obj: any, isClient = false) => {
      if (!obj) return "—";
      if (isClient) {
        const n = `${obj.nomClient ?? ""} ${obj.prenomClient ?? ""}`.trim();
        return n || obj.raisonSociale || "—";
      }
      return `${obj.prenom ?? ""} ${obj.nom ?? ""}`.trim() || "—";
    };

    const initiales = (user: any) => {
      if (!user) return "?";
      const p = user.prenom?.[0] ?? "";
      const n = user.nom?.[0] ?? "";
      return (p + n).toUpperCase() || "?";
    };

    const formatMontant = (val: any) => {
      const n = parseFloat(val ?? 0);
      return new Intl.NumberFormat("fr-FR").format(n);
    };

    const formatDate = (val: any) => {
      if (!val) return "—";
      try {
        return format(new Date(val), "dd/MM/yyyy HH:mm", { locale: fr });
      } catch {
        return val;
      }
    };

    const badgeClass = (statut: string) => {
      switch (statut) {
        case "Validée": return "bg-success";
        case "Rejetée": return "bg-danger";
        default: return "bg-warning text-dark";
      }
    };

    onMounted(() => charger(1));

    return {
      demandes, loading, page, totalPages, totalElements, limit,
      searchTerm, filtreStatut,
      rejeterModalRef, demandeSelectionnee, motifRejet,
      charger, handlePaginate, valider, ouvriRejeter, confirmerRejet,
      nomComplet, initiales, formatMontant, formatDate, badgeClass,
    };
  },
});
</script>
